import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ChevronDown, Loader2, ArrowRight, ArrowLeft, ExternalLink } from 'lucide-react'
import { IntegrationCodeInput } from '@/components/auth/IntegrationCodeInput'
import { authService } from '@/services/auth.service'
import { useAuthStore } from '@/lib/auth-store'
import { Logo } from '@/components/ui/Logo'

type Step = 'email' | 'code'

export function LoginPage() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  const [step, setStep] = useState<Step>('email')
  const [code, setCode] = useState('')
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)

  const handleEmailSubmit = async () => {
    if (!email.trim()) {
      setError('Informe seu e-mail')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      await authService.verifyEmail(email)
      setStep('code')
      setError('')
    } catch (err: any) {
      setError(err.message || 'E-mail não autorizado')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeSubmit = async () => {
    if (code.length !== 15) {
      setError('Código deve ter 15 caracteres')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      const { token, user } = await authService.validateCode(code)
      setAuth(token, user)
      navigate('/dashboard')
    } catch (err: any) {
      setError(err.message || 'Erro ao validar código')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    setStep('email')
    setCode('')
    setError('')
  }

  const handleSubmit = step === 'email' ? handleEmailSubmit : handleCodeSubmit
  const isReady = step === 'email' ? email.trim().length > 0 : code.length === 15

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.03)_0%,transparent_70%)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md animate-fade-in">
        {/* Header */}
        <div className="text-center mb-10">
          <Link
            to="/"
            className="inline-block mb-6 transition-transform hover:scale-105"
          >
            <Logo size="lg" className="text-white text-2xl" />
          </Link>
          <p className="text-gray-500 text-sm">
            Conecte-se à sua conta Futura Broker
          </p>
        </div>

        {/* Main Card */}
        <div className="relative">
          <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-white/10 to-transparent opacity-50" />

          <div className="relative bg-black/40 backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 space-y-6">
            {/* Step Email */}
            {step === 'email' && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <h1 className="text-xl font-semibold text-white tracking-tight">
                    Informe seu e-mail
                  </h1>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Use um e-mail autorizado
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-2">E-mail</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError('') }}
                      onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                      disabled={isLoading}
                      placeholder="seu@email.com"
                      className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-4 py-3.5 text-white placeholder-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all"
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-red-400">{error}</p>
                  )}
                </div>
              </div>
            )}

            {/* Step Code */}
            {step === 'code' && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Voltar</span>
                  </button>
                  <h1 className="text-xl font-semibold text-white tracking-tight">
                    Código de Integração
                  </h1>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Insira o código de 15 dígitos gerado na sua conta Futura Broker
                  </p>
                </div>

                <IntegrationCodeInput
                  value={code}
                  onChange={setCode}
                  onSubmit={handleCodeSubmit}
                  disabled={isLoading}
                  error={error}
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!isReady || isLoading}
              className={`group relative w-full py-4 rounded-xl font-medium transition-all duration-300 overflow-hidden ${
                isReady && !isLoading
                  ? 'bg-white text-black hover:bg-gray-100'
                  : 'bg-white/10 text-white/40 cursor-not-allowed'
              }`}
            >
              {isReady && !isLoading && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              )}

              <span className="relative flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{step === 'email' ? 'Verificando...' : 'Conectando...'}</span>
                  </>
                ) : (
                  <>
                    <span>{step === 'email' ? 'Continuar' : 'Conectar'}</span>
                    <ArrowRight className={`w-4 h-4 transition-transform ${isReady ? 'group-hover:translate-x-1' : ''}`} />
                  </>
                )}
              </span>
            </button>

            {/* Instructions (only for code step) */}
            {step === 'code' && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/[0.06]" />
                  </div>
                  <div className="relative flex justify-center">
                    <button
                      onClick={() => setShowInstructions(!showInstructions)}
                      className="bg-black px-4 text-xs text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1.5"
                    >
                      <span>Onde encontrar meu código?</span>
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform duration-300 ${showInstructions ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </div>
                </div>

                <div
                  className={`overflow-hidden transition-all duration-500 ease-out ${
                    showInstructions ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pt-2 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-white">Como obter seu código</h3>
                      <a
                        href="https://futurabroker.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-white transition-colors"
                      >
                        <span>Abrir Futura Broker</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>

                    <ol className="space-y-3">
                      {[
                        'Acesse futurabroker.com e faça login',
                        'Clique em Configurações (ícone de engrenagem)',
                        'Na sidebar, procure por "Integrações"',
                        'Clique em "Integração com terceiros"',
                        'Clique em "Gerar código de integração"',
                        'Copie o código de 15 dígitos e cole aqui',
                      ].map((step, index) => (
                        <li key={index} className="flex gap-3 text-sm">
                          <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-white/[0.06] text-[10px] font-medium text-gray-400">
                            {index + 1}
                          </span>
                          <span className="text-gray-400 leading-5">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-600 mt-8">
          Ao conectar, você concorda com nossos{' '}
          <Link to="/terms" className="text-gray-500 hover:text-white transition-colors">
            Termos de Uso
          </Link>
          {' '}e{' '}
          <Link to="/privacy" className="text-gray-500 hover:text-white transition-colors">
            Política de Privacidade
          </Link>
        </p>
      </div>
    </div>
  )
}
